import { useEffect } from 'react';
import { QBodyLayout, QCenter, QNotFound, QSpinner, QStack, useCurrentUser } from '@qualio/ui-components';
import { EventFormViewHeader } from './EventFormViewHeader';
import { EventFormProperties } from './EventFormViewProperties';
import { EventFormViewDetailsReadOnly } from './EventFormViewDetailsReadOnly';
import { EventFormViewComments } from './EventFormViewComments';
import { EventFormViewDetailsEdit } from './EventFormViewDetailsEdit';
import { useFieldArray, useForm } from 'react-hook-form';
import { EventFormFieldsUpdate, EventFormFieldsUpdateSchema } from '../../EventStepForm/components/EventFormBody/types';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useCurrentUserFormApproverOrReviewer,
  useEventDetails,
  useEventFormParams,
  useEventFormUpdate,
} from '../../../hooks';
import { useFormDetails } from '../../../hooks/useEventForm';
import { useEventFormReviewers } from '../../../hooks/useEventFormReviewers';
import { useEventFormApprovers } from '../../../hooks/useEventFormApprovers';
import { useEventFormComments } from '../../../hooks/useEventFormComments';
import { EditorUploadProvider, ReferenceDrawerProvider } from '../../../context';
import { FormStatusEnum } from '../../../types';
import { useFilterApprovers } from '../../../hooks/useFilterApprovers';

type EventStepFormProps = {
  viewType: 'view' | 'edit';
};

export const EventFormView = ({ viewType }: EventStepFormProps) => {
  const { companyId, userId } = useCurrentUser();

  const { eventId, stepId, formId } = useEventFormParams();

  const { data: formData, isLoading: formIsLoading, isError: formIsError } = useFormDetails(eventId, stepId, formId);
  const { data: eventData, isLoading: eventIsLOading, isError: eventIsError } = useEventDetails(eventId);

  const { mutate: updateForm, isLoading: isUpdateFormLoading } = useEventFormUpdate();

  const formMethods = useForm<EventFormFieldsUpdate>({
    mode: 'onChange',
    resolver: zodResolver(EventFormFieldsUpdateSchema),
  });
  const { control, reset } = formMethods;
  const { fields } = useFieldArray({ control, name: 'fields', keyName: 'customId' });
  const { handleSubmit } = formMethods;

  useEffect(() => {
    reset(
      EventFormFieldsUpdateSchema.parse({
        fields: formData?.fields ?? [],
      }),
    );
  }, [reset, formData]);

  const { data: reviewers, isLoading: isReviewersLoading } = useEventFormReviewers(formId);
  const { data: approvers, isLoading: isApproversLoading } = useEventFormApprovers(formId);
  const comments = useEventFormComments(reviewers ?? [], approvers ?? []);

  const { isCurrentUserApprover, isCurrentUserReviewer } = useCurrentUserFormApproverOrReviewer(
    approvers ?? [],
    reviewers ?? [],
  );
  const filteredApprovers = useFilterApprovers(approvers ?? []);
  const isNoQualityApprover = filteredApprovers('quality')?.length === 0;
  const showApprovalRequestAlert = isCurrentUserApprover && formData?.status === 'for_approval';
  const showReviewRequestAlert = isCurrentUserReviewer && formData?.status === 'for_review';

  const isEditView = viewType === 'edit' && formData?.status === 'draft' && formData?.owner_id === userId;
  const isApiLoading =
    formIsLoading ||
    eventIsLOading ||
    isReviewersLoading ||
    isApproversLoading ||
    formData === undefined ||
    eventData === undefined;

  // The form or its parent event couldn't be fetched (e.g. a 404 for a deleted / non-existent
  // form, or another fetch failure after retries). Show the not-found page instead of spinning.
  if ((formIsError && !formData) || (eventIsError && !eventData)) {
    return <QNotFound data-cy="event-form-not-found" />;
  }

  if (isApiLoading) {
    return (
      <QCenter>
        <QSpinner />
      </QCenter>
    );
  }

  const currentStep = eventData.steps.find((s) => s.id === stepId);
  const qualityApprovalRequired = currentStep?.quality_approval_required ?? true;

  const saveForm = handleSubmit((data) => updateForm(data.fields.map((field) => field.values).flat()));

  return (
    <EditorUploadProvider>
      <QBodyLayout.Default>
        <EventFormViewHeader
          data={formData}
          isEditMode={isEditView}
          showApprovalRequestAlert={showApprovalRequestAlert}
          showReviewRequestAlert={showReviewRequestAlert}
          saveForm={saveForm}
          isUpdateFormLoading={isUpdateFormLoading}
          isNoQualityApprover={isNoQualityApprover}
        />
        <ReferenceDrawerProvider>
          <QStack>
            {isEditView ? (
              <EventFormViewDetailsEdit control={control} fields={fields} />
            ) : (
              <>
                <EventFormViewDetailsReadOnly data={formData} />
                {comments.length > 0 &&
                  [FormStatusEnum.Approved, FormStatusEnum.Superseded].includes(formData.status as FormStatusEnum) && (
                    <EventFormViewComments comments={comments} />
                  )}
              </>
            )}
          </QStack>
          {eventData && formData && (
            <EventFormProperties
              event={eventData.issue}
              reviewers={reviewers ?? []}
              approvers={approvers ?? []}
              form={formData}
              companyId={companyId}
              stepId={stepId}
              qualityApprovalRequired={qualityApprovalRequired}
            />
          )}
        </ReferenceDrawerProvider>
      </QBodyLayout.Default>
    </EditorUploadProvider>
  );
};
